import { Bar } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  type ChartOptions,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import { useIsDarkMode } from "~/hooks/useIsDarkMode";
import { type CostGranularity, costTypeLabel, type PeriodCostSummary } from "./costSummary";
import { CHART_AXIS_TEXT_COLOR, CHART_GRID_COLOR, costTypeColor, formatPeriod } from "./theme";

// Registered at module load so the elements exist before the chart mounts.
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

interface CostChartProps {
  periods: PeriodCostSummary[];
  granularity: CostGranularity;
  // Cost types in color-slot order; a cost type's slot is its index here.
  costTypes: string[];
  formatCost: (amount: number) => string;
}

/** Spend per day, week or month, stacked by cost type. */
export const CostChart = ({ periods, granularity, costTypes, formatCost }: CostChartProps) => {
  const mode = useIsDarkMode() ? "dark" : "light";
  // A dataset per cost type, a data point per period.
  const amountAt = (periodIndex: number, costTypeIndex: number) =>
    periods[periodIndex].byType[costTypes[costTypeIndex]] ?? 0;

  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        position: "bottom",
        labels: { color: CHART_AXIS_TEXT_COLOR[mode], boxWidth: 10, boxHeight: 10 },
      },
      tooltip: {
        filter: (item) => amountAt(item.dataIndex, item.datasetIndex) > 0,
        callbacks: {
          label: (item) =>
            `${item.dataset.label}: ${formatCost(amountAt(item.dataIndex, item.datasetIndex))}`,
          // A period with no cost has every item filtered out; an empty footer
          // leaves the tooltip with no content, so chart.js skips drawing it.
          footer: (items) =>
            items.length > 0 ? `Total: ${formatCost(periods[items[0].dataIndex].total)}` : "",
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { color: CHART_AXIS_TEXT_COLOR[mode] },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: CHART_GRID_COLOR[mode] },
        border: { display: false },
        ticks: {
          color: CHART_AXIS_TEXT_COLOR[mode],
          callback: (value) => formatCost(Number(value)),
        },
      },
    },
  };

  return (
    <div className="h-64">
      <Bar
        options={options}
        data={{
          labels: periods.map(({ periodStart }) => formatPeriod(periodStart, granularity)),
          datasets: costTypes.map((costType, slot) => ({
            label: costTypeLabel(costType),
            data: periods.map((_, periodIndex) => amountAt(periodIndex, slot)),
            backgroundColor: costTypeColor(slot, mode),
            // A transparent top edge leaves a gap between stacked segments.
            borderColor: "transparent",
            borderWidth: { top: 2 },
            maxBarThickness: 48,
          })),
        }}
      />
    </div>
  );
};
